import { CLIENT_CONTENT_READY_EVENT } from './client-content-ready'

declare global {
  interface Window {
    __trellisSentryReplayScheduled?: boolean
  }
}

/** Error capture and tracing are already active. Only DOM recording waits for
 * content, then an idle turn. Other routes and failed loads have a bounded
 * fallback; an early browser error starts recording immediately. */
export function scheduleDeferredReplay(
  start: () => Promise<void>,
  onError: (error: unknown) => void
) {
  if (typeof window === 'undefined' || window.__trellisSentryReplayScheduled) {
    return
  }
  window.__trellisSentryReplayScheduled = true
  let started = false
  let fallback: number | undefined
  let idle: number | undefined
  const retryStart = () => {
    start().catch(onError)
  }
  const handleStartError = (error: unknown) => {
    onError(error)
    // A transient chunk failure must not permanently disable recording.
    window.addEventListener('online', retryStart, { once: true })
  }
  const startOnce = () => {
    if (started) {
      return
    }
    started = true
    window.clearTimeout(fallback)
    if (idle !== undefined) {
      window.cancelIdleCallback?.(idle)
    }
    window.removeEventListener('load', afterLoad)
    window.removeEventListener(CLIENT_CONTENT_READY_EVENT, afterContent)
    window.removeEventListener('error', startOnce)
    window.removeEventListener('unhandledrejection', startOnce)
    start().catch(handleStartError)
  }
  const afterContent = () => {
    if (idle !== undefined || started) {
      return
    }
    if (window.requestIdleCallback) {
      idle = window.requestIdleCallback(startOnce, { timeout: 1000 })
    } else {
      window.setTimeout(startOnce, 0)
    }
  }
  const afterLoad = () => {
    fallback = window.setTimeout(startOnce, 5000)
  }
  window.addEventListener(CLIENT_CONTENT_READY_EVENT, afterContent)
  window.addEventListener('error', startOnce)
  window.addEventListener('unhandledrejection', startOnce)
  if (document.readyState === 'complete') {
    afterLoad()
  } else {
    window.addEventListener('load', afterLoad, { once: true })
  }
  if (window.__trellisClientContentReady) {
    afterContent()
  }
}
